import type { NostrEvent } from '@nostrify/nostrify';

/** Custom Nostr event kind for shared grocery lists. See NIP.md. */
export const GROCERY_LIST_KIND = 36899;

/** Default `d`-tag value when the user hasn't named their list. */
export const DEFAULT_LIST_D = 'default';

/** Maximum allowed length of an item name (defensive against pasting). */
export const MAX_ITEM_NAME_LENGTH = 200;
/** Maximum allowed length of a quantity string. */
export const MAX_ITEM_QTY_LENGTH = 40;

export interface GroceryItem {
  id: string;
  name: string;
  qty?: string;
  checked: boolean;
  /**
   * Hex pubkey of the participant who originally added this item.
   *
   * **Trust note**: this field is inside the encrypted JSON, so a malicious
   * partner could write any string here. The UI must NOT trust it for
   * authorship-sensitive decisions; see `attributeItemsToAuthor` below,
   * which rewrites this field to the event's `pubkey` on decrypt.
   */
  addedBy: string;
  addedAt: number;
  updatedBy: string;
  updatedAt: number;
}

/**
 * Reject anything that doesn't match a 64-character lowercase hex pubkey.
 * Nostr pubkeys are canonically lowercase hex; relays and signers normalize
 * before validation, but anything user-supplied (e.g. a `p` tag from a
 * partner's event) must be checked before we use it as a query author or
 * as an ECDH peer.
 */
export function isValidPubkey(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

/** Type guard for a single grocery item from untrusted JSON. */
export function isGroceryItem(value: unknown): value is GroceryItem {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (
    typeof v.id !== 'string' ||
    v.id.length === 0 ||
    v.id.length > 100 ||
    typeof v.name !== 'string' ||
    v.name.length === 0 ||
    v.name.length > MAX_ITEM_NAME_LENGTH ||
    typeof v.checked !== 'boolean' ||
    !isValidPubkey(v.addedBy) ||
    typeof v.addedAt !== 'number' ||
    !Number.isFinite(v.addedAt) ||
    !isValidPubkey(v.updatedBy) ||
    typeof v.updatedAt !== 'number' ||
    !Number.isFinite(v.updatedAt)
  ) {
    return false;
  }
  if (
    v.qty !== undefined &&
    (typeof v.qty !== 'string' || v.qty.length > MAX_ITEM_QTY_LENGTH)
  ) {
    return false;
  }
  return true;
}

/**
 * Parse a JSON string of grocery items, filtering out anything malformed.
 * Returns `[]` for empty or invalid input rather than throwing.
 */
export function parseItemsJson(json: string | undefined | null): GroceryItem[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isGroceryItem);
  } catch {
    return [];
  }
}

/**
 * Override each item's `addedBy` / `updatedBy` to be the event's `pubkey`.
 *
 * The decrypted JSON is part of an event whose author signed it — so we
 * know the *event* came from `event.pubkey` (Nostrify verified the
 * signature). What we DON'T know is who wrote any given field inside the
 * JSON. A malicious partner could put anyone's pubkey in `addedBy` and
 * forge identity in the merged UI. By stamping the event's pubkey over
 * every item, we make authorship match what we can actually prove.
 *
 * In a couple's grocery list this is mostly belt-and-suspenders, but it's
 * a clean trust-boundary discipline: data inside an event is only
 * authoritative *for that event's author*.
 */
export function attributeItemsToAuthor(
  items: GroceryItem[],
  authorPubkey: string,
): GroceryItem[] {
  return items.map((item) => ({
    ...item,
    addedBy: authorPubkey,
    updatedBy: authorPubkey,
  }));
}

/**
 * Merge multiple already-decrypted item arrays (e.g. one from each spouse)
 * into a single canonical list. Items are merged by `id`, keeping the
 * version with the highest `updatedAt`.
 */
export function mergeItems(itemSets: GroceryItem[][]): GroceryItem[] {
  const byId = new Map<string, GroceryItem>();
  for (const set of itemSets) {
    for (const item of set) {
      const existing = byId.get(item.id);
      if (!existing || item.updatedAt > existing.updatedAt) {
        byId.set(item.id, item);
      }
    }
  }
  // Sort: unchecked first (newest first), then checked (newest first).
  return [...byId.values()].sort((a, b) => {
    if (a.checked !== b.checked) return a.checked ? 1 : -1;
    return b.addedAt - a.addedAt;
  });
}

/**
 * Read the participant pubkeys (`p` tags) from a list event, dropping
 * anything that isn't a valid hex pubkey. Defensive against malformed
 * tags from third-party publishers.
 */
export function getParticipants(event: NostrEvent): string[] {
  if (!event?.tags) return [];
  return event.tags
    .filter(([n]) => n === 'p')
    .map(([, v]) => v)
    .filter(isValidPubkey);
}

/** Read the title (`title` tag) from a list event. */
export function getListTitle(event: NostrEvent): string | undefined {
  if (!event?.tags) return undefined;
  return event.tags.find(([n]) => n === 'title')?.[1];
}

/**
 * Determine which peer pubkey to use when decrypting / encrypting an event.
 *
 * For an event with a `p` tag, the encryption was done between the author
 * and the tagged participant — so when *you* read it, the peer is whichever
 * of those two isn't you. For an event without a (valid) `p` tag, the
 * author encrypted to themselves and the peer is the author's own pubkey.
 */
export function resolveEncryptionPeer(
  event: Pick<NostrEvent, 'pubkey' | 'tags'>,
  myPubkey: string,
): string {
  const partner = (event.tags ?? [])
    .find(([n, v]) => n === 'p' && isValidPubkey(v))
    ?.[1];
  if (!partner) return event.pubkey; // self-encrypted
  // If we're the author, the peer is the tagged partner.
  if (event.pubkey === myPubkey) return partner;
  // Otherwise we're the tagged partner; the peer is the author.
  return event.pubkey;
}

/**
 * Strip control characters from a free-text user input and clamp its
 * length. We allow ordinary unicode (so "Crème fraîche" works), but reject
 * C0/C1 control characters except for newline-as-space.
 */
export function sanitizeFreeText(value: string, maxLength: number): string {
  // Collapse newlines to spaces, then strip remaining control characters.
  // The unicode property \p{C} covers control + format + surrogate +
  // private-use + unassigned, which is appropriately conservative.
  const cleaned = value
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\p{C}/gu, '')
    .trim();
  return cleaned.slice(0, maxLength);
}

/**
 * Generate a stable id for a new item. We require a secure-context
 * `crypto.randomUUID` — collisions in the merge layer are a correctness
 * issue (one spouse's item could overwrite the other's), so we'd rather
 * fail loudly than silently fall back to a low-entropy alternative.
 */
export function newItemId(): string {
  if (typeof crypto === 'undefined' || !('randomUUID' in crypto)) {
    throw new Error(
      'A secure browser context is required to create new items. ' +
        'Reload the page over HTTPS and try again.',
    );
  }
  return crypto.randomUUID();
}
